import { getApp, getApps, initializeApp } from 'firebase/app';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getAuth,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';

type FirebaseWebConfig = {
  apiKey: string;
  authDomain: string;
  projectId: string;
  appId: string;
  messagingSenderId?: string;
  storageBucket?: string;
};

const readEnv = (key: string): string => {
  const value = (import.meta.env[key] as string | undefined) ?? '';
  return value.trim();
};

const firebaseMessagingSenderId = readEnv('VITE_FIREBASE_MESSAGING_SENDER_ID');
const firebaseStorageBucket = readEnv('VITE_FIREBASE_STORAGE_BUCKET');
const googleClientId = readEnv('VITE_GOOGLE_CLIENT_ID') || readEnv('VITE_FIREBASE_GOOGLE_CLIENT_ID');

const firebaseConfig: FirebaseWebConfig = {
  apiKey: readEnv('VITE_FIREBASE_API_KEY'),
  authDomain: readEnv('VITE_FIREBASE_AUTH_DOMAIN'),
  projectId: readEnv('VITE_FIREBASE_PROJECT_ID'),
  appId: readEnv('VITE_FIREBASE_APP_ID'),
  ...(firebaseMessagingSenderId ? { messagingSenderId: firebaseMessagingSenderId } : {}),
  ...(firebaseStorageBucket ? { storageBucket: firebaseStorageBucket } : {}),
};

const requiredConfigMissing =
  !firebaseConfig.apiKey || !firebaseConfig.authDomain || !firebaseConfig.projectId || !firebaseConfig.appId;

let cachedAuth: ReturnType<typeof getAuth> | null = null;
let initialAuthStatePromise: Promise<void> | null = null;
const AUTH_STATE_READY_TIMEOUT_MS = 3000;

const getFirebaseAuth = () => {
  if (requiredConfigMissing) return null;
  if (cachedAuth) return cachedAuth;
  const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
  cachedAuth = getAuth(app);
  return cachedAuth;
};

export const isFirebaseAuthConfigured = (): boolean => !requiredConfigMissing;
export const isGoogleIdentityConfigured = (): boolean => Boolean(googleClientId);

type GoogleCredentialResponse = {
  credential?: string;
};

type GoogleButtonOptions = {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
};

type GoogleAccountsId = {
  initialize: (options: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
  }) => void;
  renderButton: (element: HTMLElement, options: GoogleButtonOptions) => void;
};

type GoogleApiWindow = Window & {
  google?: {
    accounts?: {
      id?: GoogleAccountsId;
    };
  };
};

type RenderGoogleIdentityButtonOptions = {
  onSuccess?: () => Promise<void> | void;
  onError?: (message: string) => void;
};

let googleScriptPromise: Promise<void> | null = null;

const loadGoogleIdentityScript = async (): Promise<void> => {
  const scriptId = 'google-identity-services';
  const existing = document.getElementById(scriptId);
  if (existing) return;
  if (!googleScriptPromise) {
    googleScriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Identity Services.'));
      document.head.appendChild(script);
    });
  }
  await googleScriptPromise;
};

export const renderGoogleIdentityButton = async (
  container: HTMLElement,
  options?: RenderGoogleIdentityButtonOptions
): Promise<void> => {
  if (!googleClientId) throw new Error('Google Identity client id is not configured.');
  await loadGoogleIdentityScript();
  const auth = getFirebaseAuth();
  if (!auth) throw new Error('Firebase auth is not configured.');
  const googleApi = (window as GoogleApiWindow).google?.accounts?.id;
  if (!googleApi) throw new Error('Google Identity Services unavailable.');

  googleApi.initialize({
    client_id: googleClientId,
    callback: async (response) => {
      try {
        if (!response.credential) throw new Error('Google credential was missing.');
        const credential = GoogleAuthProvider.credential(response.credential);
        await signInWithCredential(auth, credential);
        await options?.onSuccess?.();
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Google sign-in failed.';
        options?.onError?.(message);
      }
    },
  });

  container.innerHTML = '';
  googleApi.renderButton(container, {
    type: 'standard',
    theme: 'filled_blue',
    size: 'large',
    text: 'continue_with',
    shape: 'pill',
    logo_alignment: 'left',
    width: 260,
  });
};

export const subscribeFirebaseUser = (
  onChange: (user: User | null) => void
): (() => void) => {
  const auth = getFirebaseAuth();
  if (!auth) {
    onChange(null);
    return () => {};
  }
  return onAuthStateChanged(auth, onChange);
};

export const getCurrentFirebaseUser = (): User | null => {
  const auth = getFirebaseAuth();
  return auth?.currentUser ?? null;
};

const waitForInitialAuthState = async (): Promise<void> => {
  const auth = getFirebaseAuth();
  if (!auth) return;
  const authWithReady = auth as typeof auth & { authStateReady?: () => Promise<void> };
  if (typeof authWithReady.authStateReady === 'function') {
    await authWithReady.authStateReady();
    return;
  }

  if (!initialAuthStatePromise) {
    initialAuthStatePromise = new Promise<void>((resolve) => {
      let unsubscribe: (() => void) | undefined;
      unsubscribe = onAuthStateChanged(
        auth,
        () => {
          unsubscribe?.();
          resolve();
        },
        () => {
          unsubscribe?.();
          resolve();
        }
      );
    });
  }
  await initialAuthStatePromise;
};

export const getFirebaseIdToken = async (): Promise<string | null> => {
  const auth = getFirebaseAuth();
  if (!auth) return null;
  await Promise.race([
    waitForInitialAuthState(),
    new Promise<void>((resolve) => {
      setTimeout(resolve, AUTH_STATE_READY_TIMEOUT_MS);
    }),
  ]);
  if (!auth.currentUser) return null;
  return await auth.currentUser.getIdToken();
};

export const signInWithGoogle = async (): Promise<void> => {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error('Firebase auth is not configured.');
  const provider = new GoogleAuthProvider();
  await signInWithPopup(auth, provider);
};

export const signInWithEmailPassword = async (email: string, password: string): Promise<void> => {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error('Firebase auth is not configured.');
  await signInWithEmailAndPassword(auth, email.trim(), password);
};

export const registerWithEmailPassword = async (email: string, password: string): Promise<void> => {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error('Firebase auth is not configured.');
  await createUserWithEmailAndPassword(auth, email.trim(), password);
};

export const sendPasswordReset = async (email: string): Promise<void> => {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error('Firebase auth is not configured.');
  await sendPasswordResetEmail(auth, email.trim());
};

export const signOutFirebase = async (): Promise<void> => {
  const auth = getFirebaseAuth();
  if (!auth) return;
  await signOut(auth);
};
